import { LayerDTO } from 'business/models/Models';
import { showAlertSnackBar } from 'utils/Global';
import { DragContext } from 'components/DraggableContainer/DragContextProvider';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  DragRectangleProps,
  DragWellDetail,
  dropTargetNodeTypesMap,
  treeNodeTypeToLayerTypeMap,
} from 'components/plateviewer/experiment/canvasDragAndDrop/constants';
import React, { useState, useContext } from 'react';
import { addNewLayer } from 'components/plateviewer/experiment/utils';
import {
  addLayerDetails,
  getLayerDimensions,
  getLayerRectDimensions,
  getOverlayRectangleProps,
  isLayerDragDropAllowedType,
} from './utils';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useRMSelector } from 'redux/hooks';
import { selectIsEditable } from 'redux/selectors/StateSelectors';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { useModalHandlers } from 'components/modal';
import { ScreenToCustomlayerWarning } from '.';
import { useIsScreenInUse } from 'redux/hooks/common';

type DropHandlerProps = {
  stageRef: any;
  exp: ExperimentDtoClient;
  updateLayerStates: (layer: LayerDTO, isShowLayerEditPanel?: boolean) => void;
  experimentId: number;
  isScreenRoute?: boolean;
};
export const useCanvasDragDropHandlers = (props: DropHandlerProps) => {
  const { exp, updateLayerStates, experimentId, stageRef, isScreenRoute } = props;
  const [dragOverlayRect, setDragOverlayRect] = useState<DragRectangleProps>(
    new DragRectangleProps(),
  );
  const [layerOverlayRect, setLayerOverlayRect] = useState<DragRectangleProps>(
    new DragRectangleProps(),
  );
  const [dragWellDetail, setDragWellDetail] = useState<DragWellDetail>();
  const { draggedNodes, iconRef, tooltipRef } = useContext(DragContext);
  const isAddLayerDisabled = useIsAddLayerDisabled(exp);
  const { showModal, hideModal } = useModalHandlers();

  const removeDragIcon = () => {
    if (document.body.contains(iconRef.current)) {
      document.body.removeChild(iconRef.current);
    }
    if (document.body.contains(tooltipRef.current)) {
      document.body.removeChild(tooltipRef.current);
    }
  };

  const onDrop = async () => {
    removeDragIcon();
    setDragOverlayRect((prev) => ({ ...prev, visible: false }));
    setLayerOverlayRect((prev) => ({ ...prev, visible: false }));

    const node = draggedNodes?.[0];

    if (!node || isAddLayerDisabled || !isLayerDragDropAllowedType(node?.type)) {
      return;
    }

    const { type, id, extra } = node;
    const { dropNumber } = dragWellDetail;
    const usedWellCount = extra?.conditionsCount;
    const addLayerOnCanvas = async () => {
      hideModal();
      const layerDimensions = getLayerDimensions(
        exp,
        type,
        dragWellDetail,
        usedWellCount,
      );
      try {
        let layer = addNewLayer(
          layerDimensions.x,
          layerDimensions.y,
          layerDimensions.width,
          layerDimensions.height,
          treeNodeTypeToLayerTypeMap[type],
          exp,
          experimentId,
        );
        layer = await addLayerDetails(
          type,
          dropNumber,
          layer,
          exp,
          id,
          node,
          extra,
          isScreenRoute,
          true, // fromLayersExplorer
        );
        if (layer) {
          updateLayerStates(layer, true);
        }
      } catch (err) {
        showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.BOTTOM, err.message);
      }
    };

    if (isScreenRoute && type === ETreeNodeTypes.Screen) {
      showModal({
        heading: 'Warning',
        body: <ScreenToCustomlayerWarning />,
        onOk: addLayerOnCanvas,
        onCancel: hideModal,
        okButtonText: 'Yes',
        noButtonText: 'No',
        id: 'customWidthModal',
      });
    } else {
      addLayerOnCanvas();
    }
  };

  const onDragOver = (event) => {
    const node = draggedNodes?.[0];

    if (isAddLayerDisabled || !isLayerDragDropAllowedType(node?.type)) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    const isDropTarget = dropTargetNodeTypesMap[node.type];

    if (isDropTarget === undefined) {
      return;
    }
    const usedWellCount = node.extra?.conditionsCount;
    const { dragRectProps, dragWellDetail } = getOverlayRectangleProps(
      stageRef,
      event.clientX,
      event.clientY,
      isDropTarget,
    );
    event.dataTransfer.dropEffect = dragRectProps.visible ? 'move' : 'none';

    setDragOverlayRect(dragRectProps);
    setDragWellDetail(dragWellDetail);

    if (dragWellDetail) {
      const layerRect = getLayerRectDimensions(
        exp,
        node.type,
        dragWellDetail,
        usedWellCount,
      );
      if (layerRect) {
        setLayerOverlayRect({
          ...layerRect,
          visible: true,
        });
      }
    }
  };

  const onDragLeave = () => {
    setDragOverlayRect((prev) => ({ ...prev, visible: false }));
    setLayerOverlayRect((prev) => ({ ...prev, visible: false }));
  };

  return { onDrop, onDragOver, dragOverlayRect, onDragLeave, layerOverlayRect };
};

export const useIsAddLayerDisabled = (exp: ExperimentDtoClient) => {
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const isScreenInUse: boolean = useIsScreenInUse();
  const accessRightName = exp?.accessRightName || '';
  return (
    !(accessRightName === ETreeNodeAccessRightNames.Modify) ||
    !isEditMode ||
    !exp.layers ||
    isScreenInUse
  );
};
